// form-controls.ts — THE form-control primitives (Styles × Themes program,
// PR-3a).
//
// One machine per control family, for all five surfaces. It lives in
// pi-stack/src/client for the same reason tooltip.ts and buttons.ts do: this
// is the shared client layer the Helm, both portals, the Bridge home and the
// pi chat all consume, and helm-bridge/src/client/form-controls.ts and
// helm-console/src/client/form-controls.ts are RE-EXPORTS, never copies.
//
// ── what this bans ────────────────────────────────────────────────────────
// The WF-A audit measured, across the five surfaces: 32 native <select>, 24
// native number spinners, 20 native checkboxes (ZERO of them a grid
// bulk-select, so the ruling's one carve-out was entirely unused), and no
// date/time control at all. A native <select>'s OPEN list is OS chrome and
// cannot theme; a number spinner's arrows are OS chrome and cannot theme; a
// checkbox's box is OS chrome and cannot theme. On a product whose whole
// point is a style × theme axis, every one of those is a hole in the skin.
// primitives-check refuses all four by file:line.
//
// ── the ONE dropdown ──────────────────────────────────────────────────────
// Two custom dropdowns already existed — the Helm's `mkDropdown` in glance.ts
// and the console's twin in dropdown.ts — with the same listbox contract and
// different bugs (the Helm's had no keyboard lane at all, no focus-out
// dismiss, no type-ahead). They are now ONE machine wearing two skins:
// `mkFcSelect` always emits the `fc-*` classes (which is what PAINTS) and
// additionally whatever legacy classes the caller's `skin` names (which is
// what POSITIONS, and what the existing suites and e2e specs address). A
// skin can never fork the behaviour, only the layout hooks.
//
// ── the composite rule ────────────────────────────────────────────────────
// A stepper's ± halves and a listbox's option rows are NOT buttons in their
// own right (they are not even tab stops — the composite owns one). They
// carry `data-btn-role="stepper"` / `role="option"` so buttons-check counts
// them as deferred internals of a PR-3 primitive rather than demanding an
// icon-only .ibtn where an icon-only .ibtn makes no sense.
//
// ── keyboard at birth ─────────────────────────────────────────────────────
// Every control here is keyboard-usable the moment it is constructed, and
// primitives-check has an arm for it. Select: the trigger is the tab stop,
// Arrow/Enter/Space opens, arrows rove, Home/End jump, printable characters
// type-ahead, Escape closes and returns focus. Stepper: Arrow up/down and
// PageUp/PageDown step, Home/End clamp to min/max. Toggle: Space/Enter.
// Combobox: arrows + Enter/Tab accept, Escape closes the list only.
// Date/time: arrows step the focused SEGMENT.

import { mkIconBtn } from './buttons.js';

const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s: string): string => String(s).replace(/[&<>"']/g, (c) => ESC[c] as string);

const svg = (glyph: string, cls: string): string =>
  `<svg class="icon ${cls}" aria-hidden="true"><use href="#i-${esc(glyph)}"/></svg>`;

/** Every control family this module owns. primitives-check reads it. */
export const FC_FAMILIES = ['select', 'stepper', 'toggle', 'text', 'search', 'combobox', 'datetime'] as const;
export type FcFamily = (typeof FC_FAMILIES)[number];

/**
 * The `--fc-*` token slots each CSS home must define. The Helm speaks raw
 * palette names and the console speaks --hc-*, so the VALUES differ per home
 * — but the NAMES are these, and the `fc-component-block` that consumes them
 * is byte-identical across both. primitives-check refuses either drift.
 */
export const FC_TOKEN_SLOTS = [
  'field-bg', 'field-ink', 'field-border', 'field-muted',
  'field-hover-border', 'field-focus-border', 'field-focus-ring', 'field-disabled-ink',
  'pop-bg', 'pop-border', 'pop-shadow',
  'opt-hover-bg', 'opt-on-bg', 'opt-on-ink',
  'track-off', 'track-on', 'knob', 'knob-on',
  'ok', 'bad',
] as const;

/** Legacy class hooks a call site may keep for layout and for the selectors
 *  its suites already address. LAYOUT ONLY — colour belongs to the fc block,
 *  and primitives-check has the same paint arm buttons-check does. */
export interface FcSkin {
  wrap?: string;
  btn?: string;
  label?: string;
  chev?: string;
  pop?: string;
  item?: string;
  input?: string;
  /** The text/search lane's extra hooks: the leading glyph, the clear
   *  affordance, and the two STATE classes (has-text, typing) the console's
   *  search-box interaction ladder is authored against. */
  ico?: string;
  clear?: string;
  has?: string;
  typing?: string;
}

const cx = (...parts: Array<string | undefined | false>): string =>
  parts.filter(Boolean).join(' ');

/** class/id through PROPERTIES as well as the attribute — the console's
 *  stub-document harness reflects only the property, and buttons.ts learned
 *  this the hard way (a control built attribute-only was invisible to every
 *  `querySelector('.x')` in those suites). */
function setCls(el: HTMLElement, cls: string): void {
  el.className = cls;
  for (const c of cls.split(/\s+/)) if (c) el.classList?.add(c);
  el.setAttribute?.('class', cls);
}

/** `classList.toggle` with the console's stub-document harness in mind: its
 *  classList implements add/remove/contains and NOTHING else, and a
 *  primitive that reaches for `toggle` is unusable from every suite that
 *  drives a real module against that stub (measured: mkSearchBox threw on
 *  the first call). One helper, so no call site below has to remember. */
function setClass(el: Element | null | undefined, cls: string, on: boolean): void {
  if (!el || !cls) return;
  if (on) el.classList?.add(cls);
  else el.classList?.remove(cls);
}

function mk<K extends keyof HTMLElementTagNameMap>(
  tag: K, cls?: string, text?: string,
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (cls) setCls(el, cls);
  if (text !== undefined) el.textContent = text;
  return el;
}

// ═══════════════════════════════════════════════════════════ 1. SELECT

export interface FcOption {
  value: string;
  /** Plain-text label — the closed face's fallback and the type-ahead key. */
  label: string;
  /** Optional row markup (swatch + label). Caller-trusted HTML. */
  html?: string;
  /** Optional CLOSED-face markup when the open row carries more than the
   *  closed face should repeat. Falls back to `html`, then `label`. */
  labelHtml?: string;
  disabled?: boolean;
}

export interface FcSelectOpts {
  /** Lands on the TRIGGER — the focusable, aria-carrying element. */
  id?: string;
  /** A live getter (the console twin's shape) or a fixed array (the shape a
   *  converted native <select> wants). An array puts the control in charge of
   *  its own options; `setOptions` then repaints. */
  options: FcOption[] | (() => FcOption[]);
  /** A live getter, or the initial value when the control owns its state. */
  value?: string | (() => string);
  /** Fired on a real change (never on a no-op re-pick). Optional: a
   *  self-owned control also emits a 'change' listener lane. */
  onPick?: (value: string) => void;
  /** The option under pointer/focus (the console twin's preview lane). */
  onPreview?: (value: string) => void;
  /** Every exit from an active preview lands here exactly once. */
  onPreviewEnd?: () => void;
  /** Called on open so the host can close sibling popovers. */
  onOpen?: () => void;
  /** Called once per REAL close (never for a close() on an already-closed
   *  list) — the host's release for whatever it pinned on open. */
  onClose?: () => void;
  /** Accessible name for the trigger. Required by primitives-check on any
   *  select that carries no visible <label> association. */
  label?: string;
  /** Re-parent the pop to document.body on open and position it fixed at the
   *  trigger's viewport rect. REQUIRED inside a clipping/transformed host
   *  (an AG Grid cell). */
  portal?: boolean;
  /** Extra classes on the wrapper. Layout only. */
  cls?: string;
  /** Legacy class hooks (see FcSkin). */
  skin?: FcSkin;
  size?: 'sm' | 'md';
  disabled?: boolean;
}

export interface FcSelectCtl {
  el: HTMLElement;
  btn: HTMLButtonElement;
  /** Reads/writes like `HTMLSelectElement.value` — the property the ~32
   *  converted call sites were already using. */
  value: string;
  options: FcOption[];
  setOptions(options: FcOption[]): void;
  /** Re-read value() into the closed face (the console twin's `set()`). */
  set(): void;
  close(): void;
  focus(): void;
  disabled: boolean;
  /** The change lane for a control that owns its own state. */
  onChange(fn: (value: string) => void): void;
  /** `HTMLSelectElement`'s own shape, so a converted call site keeps the
   *  line it already had. The handler takes no event — there is no native
   *  event to hand it, and a call site that needed one would be reaching
   *  past the primitive. */
  addEventListener(type: 'change', fn: () => void): void;
}

/** How long a type-ahead buffer survives between keystrokes. */
export const FC_TYPEAHEAD_MS = 700;

export function mkFcSelect(opts: FcSelectOpts): FcSelectCtl {
  const skin = opts.skin ?? {};
  let ownOptions: FcOption[] = Array.isArray(opts.options) ? [...opts.options] : [];
  const readOptions = (): FcOption[] =>
    typeof opts.options === 'function' ? opts.options() : ownOptions;
  let ownValue = typeof opts.value === 'string' ? opts.value : '';
  if (typeof opts.value !== 'function' && !ownValue) ownValue = readOptions()[0]?.value ?? '';
  const readValue = (): string => (typeof opts.value === 'function' ? opts.value() : ownValue);
  const changeFns: Array<(v: string) => void> = [];

  const wrap = mk('div', cx('fc', 'fc-sel', `fc-${opts.size ?? 'md'}`, skin.wrap, opts.cls));
  // The listbox TRIGGER. Its face IS the chosen option's text, which no icon
  // can carry, and its whole job is to open the list below — `data-btn-role`
  // is how buttons-check counts it as this primitive's internal rather than
  // demanding an icon-only .ibtn here.
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.dataset.btnRole = 'trigger';
  setCls(btn, cx('fc-sel-btn', 'fc-field', skin.btn));
  if (opts.id) { btn.id = opts.id; btn.setAttribute?.('id', opts.id); }
  btn.setAttribute?.('aria-haspopup', 'listbox');
  btn.setAttribute?.('aria-expanded', 'false');
  if (opts.label) btn.setAttribute?.('aria-label', opts.label);
  btn.innerHTML =
    `<span class="${cx('fc-sel-label', skin.label)}"></span>` + svg('chevron-down', cx('fc-sel-chev', skin.chev));
  const pop = mk('div', cx('fc-pop', skin.pop));
  pop.setAttribute?.('role', 'listbox');
  if (opts.label) pop.setAttribute?.('aria-label', opts.label);
  pop.hidden = true;
  wrap.append(btn, pop);

  const labelEl = btn.querySelector<HTMLElement>('.fc-sel-label');
  const paintLabel = (): void => {
    if (!labelEl) return;
    const v = readValue();
    const cur = readOptions().find((o) => o.value === v);
    const closedHtml = cur?.labelHtml ?? cur?.html;
    if (closedHtml !== undefined) labelEl.innerHTML = closedHtml;
    else labelEl.textContent = cur?.label ?? v;
  };

  // ---- preview discipline: at most one active, every exit ends it ----
  let previewing = false;
  const previewStart = (value: string): void => { previewing = true; opts.onPreview?.(value); };
  const previewEnd = (): void => {
    if (!previewing) return;
    previewing = false;
    opts.onPreviewEnd?.();
  };

  const rows = (): HTMLButtonElement[] =>
    Array.from(pop.querySelectorAll?.<HTMLButtonElement>('.fc-opt') ?? []);

  const onDocClick = (ev: MouseEvent): void => {
    const t = ev.target as Node;
    if (!wrap.contains?.(t) && !pop.contains?.(t)) close();
  };
  const onWinMove = (): void => close();

  // ---- the ROW PIN (#1125 item 2): a list with a preview lane keeps its
  // geometry for as long as it is open. The preview restamps the document
  // (a style hover changes every font kit), and a list whose rows reflowed
  // under a pointer that did not move hands the pointer a DIFFERENT option
  // — which previews THAT one, reflows again, and finally loses the pointer
  // off the list's edge (mouseleave → the list closes before a click can
  // land). Pinning the list's box and every row's height in px makes the
  // pointer-to-option map a function of the pointer alone. Released on
  // close; the rows are rebuilt on every open anyway.
  const pinRows = (): void => {
    if (!opts.onPreview) return;
    const pr = pop.getBoundingClientRect?.();
    if (!pr || !pr.width) return;
    pop.style.width = `${pr.width}px`;
    pop.style.height = `${pr.height}px`;
    pop.style.boxSizing = 'border-box';
    for (const r of rows()) {
      const rr = r.getBoundingClientRect?.();
      if (!rr || !rr.height) continue;
      r.style.height = `${rr.height}px`;
      r.style.flex = '0 0 auto';
      r.style.boxSizing = 'border-box';
    }
  };
  const unpinRows = (): void => {
    pop.style.width = '';
    pop.style.height = '';
    pop.style.boxSizing = '';
  };

  function close(): void {
    const wasOpen = !pop.hidden;
    previewEnd();
    pop.hidden = true;
    pop.classList?.remove('on');
    btn.setAttribute?.('aria-expanded', 'false');
    document.removeEventListener?.('click', onDocClick as EventListener, true);
    unpinRows();
    if (opts.portal) {
      window.removeEventListener?.('scroll', onWinMove, true);
      window.removeEventListener?.('resize', onWinMove);
      if (pop.parentNode && pop.parentNode !== wrap) {
        pop.classList?.remove('fc-pop-portal');
        pop.style.left = '';
        pop.style.top = '';
        pop.style.minWidth = '';
        wrap.appendChild(pop);
      }
    }
    if (wasOpen) opts.onClose?.();
  }

  const commit = (value: string): void => {
    if (value === readValue()) return;
    if (typeof opts.value !== 'function') ownValue = value;
    opts.onPick?.(value);
    for (const fn of changeFns) fn(value);
  };

  const open = (focusCurrent: boolean): void => {
    if (btn.disabled) return;
    opts.onOpen?.();
    pop.replaceChildren?.();
    const cur = readValue();
    for (const o of readOptions()) {
      // An option ROW inside this primitive's listbox — role="option" is what
      // buttons-check reads to count it as a deferred internal.
      const row = document.createElement('button');
      row.type = 'button';
      setCls(row, cx('fc-opt', o.value === cur && 'on', o.disabled && 'fc-opt-off', skin.item));
      row.setAttribute('role', 'option');
      row.setAttribute?.('aria-selected', String(o.value === cur));
      row.tabIndex = -1; // roving focus; the trigger stays the tab stop
      row.dataset.value = o.value;
      row.dataset.fcLabel = o.label;
      if (o.disabled) row.disabled = true;
      if (o.html !== undefined) row.innerHTML = o.html;
      else row.textContent = o.label;
      row.addEventListener('mouseenter', () => previewStart(o.value));
      row.addEventListener('focus', () => previewStart(o.value));
      row.addEventListener('click', (e) => {
        e.stopPropagation();
        if (o.disabled) return;
        // Commit BEFORE the close path's previewEnd, so the revert repaint
        // reads the NEW committed state — one task, nothing flashes.
        commit(o.value);
        close();
        paintLabel();
        btn.focus?.();
      });
      pop.appendChild(row);
    }
    if (opts.portal) {
      pop.classList?.add('fc-pop-portal');
      document.body.appendChild(pop);
    }
    pop.hidden = false;
    pop.classList?.add('on'); // a fresh class add restarts the open animation
    if (opts.portal) {
      const r = btn.getBoundingClientRect?.();
      if (r) {
        pop.style.minWidth = `${Math.round(r.width)}px`;
        const w = pop.offsetWidth || 240;
        const h = pop.offsetHeight || 260;
        pop.style.left = `${Math.max(8, Math.min(r.left, window.innerWidth - w - 8))}px`;
        const below = r.bottom + 6;
        pop.style.top = `${below + h > window.innerHeight - 8 ? Math.max(8, r.top - h - 6) : below}px`;
      }
      window.addEventListener?.('scroll', onWinMove, true);
      window.addEventListener?.('resize', onWinMove);
    }
    // Placed and painted under the COMMITTED seat — freeze that geometry now,
    // before the first hover can preview anything.
    pinRows();
    btn.setAttribute?.('aria-expanded', 'true');
    document.addEventListener?.('click', onDocClick as EventListener, true);
    if (focusCurrent) (rows().find((r) => r.dataset.value === cur) ?? rows()[0])?.focus?.();
  };

  // ---- type-ahead: printable keys jump to the next matching option ----
  let typed = '';
  let typedAt = 0;
  const typeahead = (ch: string): void => {
    const now = Date.now();
    typed = now - typedAt > FC_TYPEAHEAD_MS ? ch : typed + ch;
    typedAt = now;
    const list = rows();
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    const norm = (s: string): string => s.toLowerCase();
    const q = norm(typed);
    // Repeat of one character walks the matches (the native <select> idiom).
    const from = typed.length === 1 ? at + 1 : at < 0 ? 0 : at;
    for (let i = 0; i < list.length; i++) {
      const r = list[(from + i + list.length) % list.length];
      if (r && norm(r.dataset.fcLabel ?? r.textContent ?? '').startsWith(q)) { r.focus?.(); return; }
    }
  };

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (pop.hidden) open(false);
    else close();
  });
  btn.addEventListener('keydown', (e) => {
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') && pop.hidden) {
      e.preventDefault();
      open(true);
    } else if (e.key === 'Escape' && !pop.hidden) {
      // Close the LIST, not the surrounding menu — the host's document-level
      // Escape owns that outer close.
      e.stopPropagation();
      close();
    } else if (pop.hidden && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      // Type-ahead from the CLOSED face picks without opening (native idiom).
      const list = readOptions();
      const hit = list.find((o) => o.label.toLowerCase().startsWith(e.key.toLowerCase()));
      if (hit) { e.preventDefault(); commit(hit.value); paintLabel(); }
    }
  });
  pop.addEventListener('click', (e) => e.stopPropagation());
  pop.addEventListener('mouseleave', () => previewEnd());
  pop.addEventListener('keydown', (e) => {
    const list = rows();
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = e.key === 'ArrowDown' ? Math.min(at + 1, list.length - 1) : Math.max(at - 1, 0);
      list[next]?.focus?.(); // focus fires the preview lane
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      (e.key === 'Home' ? list[0] : list[list.length - 1])?.focus?.();
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      close();
      btn.focus?.();
    } else if (e.key === 'Tab') {
      close();
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      typeahead(e.key);
    }
  });
  const onFocusOut = (e: FocusEvent): void => {
    const to = e.relatedTarget as Node | null;
    if (!pop.hidden && (!to || (!wrap.contains?.(to) && !pop.contains?.(to)))) close();
  };
  wrap.addEventListener('focusout', onFocusOut as EventListener);
  pop.addEventListener('focusout', onFocusOut as EventListener);

  if (opts.disabled) btn.disabled = true;
  paintLabel();

  const ctl: FcSelectCtl = {
    el: wrap,
    btn,
    get value() { return readValue(); },
    set value(v: string) {
      if (typeof opts.value !== 'function') ownValue = v;
      paintLabel();
    },
    get options() { return readOptions(); },
    setOptions(list: FcOption[]) {
      ownOptions = [...list];
      if (typeof opts.value !== 'function' && !list.some((o) => o.value === ownValue)) {
        ownValue = list[0]?.value ?? '';
      }
      if (!pop.hidden) { close(); open(false); }
      paintLabel();
    },
    set: () => paintLabel(),
    close,
    focus: () => btn.focus?.(),
    get disabled() { return btn.disabled; },
    set disabled(v: boolean) { btn.disabled = v; setClass(wrap, 'fc-off', v); },
    onChange(fn) { changeFns.push(fn); },
    addEventListener(_type, fn) { changeFns.push(() => fn()); },
  };
  return ctl;
}

// ═══════════════════════════════════════════════════════════ 2. STEPPER

export interface FcStepperOpts {
  id?: string;
  label: string;
  value?: number | string;
  min?: number;
  max?: number;
  step?: number;
  /** Rendered after the field (a unit: "px", "min", "%"). */
  unit?: string;
  placeholder?: string;
  cls?: string;
  skin?: FcSkin;
  disabled?: boolean;
  onChange?: (value: string) => void;
  onInput?: (value: string) => void;
}

export interface FcStepperCtl {
  el: HTMLElement;
  input: HTMLInputElement;
  /** String, exactly like `HTMLInputElement.value` — the converted sites read
   *  it through `Number(x.value)` and write strings. */
  value: string;
  valueAsNumber: number;
  disabled: boolean;
  focus(): void;
  addEventListener(type: 'change' | 'input', fn: () => void): void;
}

const clampStep = (raw: string, min: number, max: number, step: number): string => {
  if (raw.trim() === '') return '';
  const n = Number(raw);
  if (!Number.isFinite(n)) return '';
  const snapped = step > 0 ? Math.round((n - min) / step) * step + min : n;
  return String(Math.min(max, Math.max(min, Number(snapped.toFixed(6)))));
};

export function mkFcStepper(opts: FcStepperOpts): FcStepperCtl {
  const skin = opts.skin ?? {};
  const min = opts.min ?? Number.NEGATIVE_INFINITY;
  const max = opts.max ?? Number.POSITIVE_INFINITY;
  const step = opts.step ?? 1;
  const wrap = mk('div', cx('fc', 'fc-step', 'fc-field', skin.wrap, opts.cls));

  // The ± halves. NOT buttons in their own right — they are not tab stops and
  // carry no independent name; the composite owns both. `data-btn-role` is
  // what buttons-check reads to count them as this primitive's internals.
  const mkHalf = (dir: -1 | 1): HTMLButtonElement => {
    const b = document.createElement('button');
    b.type = 'button';
    b.dataset.btnRole = 'stepper';
    b.tabIndex = -1;
    b.setAttribute?.('aria-hidden', 'true');
    setCls(b, cx('fc-step-btn', dir < 0 ? 'fc-step-down' : 'fc-step-up'));
    b.innerHTML = svg(dir < 0 ? 'chevron-down' : 'chevron-up', 'fc-step-glyph');
    return b;
  };
  const down = mkHalf(-1);
  const up = mkHalf(1);

  const input = document.createElement('input');
  setCls(input, cx('fc-step-input', skin.input));
  // type="text" + inputmode: the native `type=number` spinner arrows are OS
  // chrome that cannot theme, and suppressing them in CSS still leaves the
  // control's scroll-wheel-eats-your-value behaviour. This is the whole
  // reason the family exists.
  input.type = 'text';
  input.setAttribute?.('inputmode', step % 1 === 0 ? 'numeric' : 'decimal');
  input.setAttribute?.('autocomplete', 'off');
  input.setAttribute?.('role', 'spinbutton');
  input.setAttribute?.('aria-label', opts.label);
  if (Number.isFinite(min)) input.setAttribute?.('aria-valuemin', String(min));
  if (Number.isFinite(max)) input.setAttribute?.('aria-valuemax', String(max));
  if (opts.id) { input.id = opts.id; input.setAttribute?.('id', opts.id); }
  if (opts.placeholder) input.placeholder = opts.placeholder;
  input.value = opts.value === undefined ? '' : String(opts.value);

  wrap.append(down, input, up);
  if (opts.unit) wrap.appendChild(mk('span', 'fc-step-unit', opts.unit));

  const changeFns: Array<() => void> = [];
  const inputFns: Array<() => void> = [];
  const fireChange = (): void => {
    input.setAttribute?.('aria-valuenow', input.value);
    opts.onChange?.(input.value);
    for (const fn of changeFns) fn();
  };
  const bump = (mult: number): void => {
    if (input.disabled) return;
    const base = input.value.trim() === '' ? (Number.isFinite(min) ? min : 0) : Number(input.value);
    const next = clampStep(String((Number.isFinite(base) ? base : 0) + step * mult), min, max, step);
    if (next === input.value) return;
    input.value = next;
    for (const fn of inputFns) fn();
    opts.onInput?.(input.value);
    fireChange();
  };
  down.addEventListener('click', () => bump(-1));
  up.addEventListener('click', () => bump(1));
  input.addEventListener('input', () => {
    for (const fn of inputFns) fn();
    opts.onInput?.(input.value);
  });
  input.addEventListener('change', () => {
    const snapped = clampStep(input.value, min, max, step);
    if (snapped !== input.value) input.value = snapped;
    fireChange();
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowUp') { e.preventDefault(); bump(1); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); bump(-1); }
    else if (e.key === 'PageUp') { e.preventDefault(); bump(10); }
    else if (e.key === 'PageDown') { e.preventDefault(); bump(-10); }
    else if (e.key === 'Home' && Number.isFinite(min)) { e.preventDefault(); input.value = String(min); fireChange(); }
    else if (e.key === 'End' && Number.isFinite(max)) { e.preventDefault(); input.value = String(max); fireChange(); }
  });
  if (opts.disabled) input.disabled = true;
  input.setAttribute?.('aria-valuenow', input.value);

  return {
    el: wrap,
    input,
    get value() { return input.value; },
    set value(v: string) { input.value = v; input.setAttribute?.('aria-valuenow', v); },
    get valueAsNumber() { return Number(input.value); },
    get disabled() { return input.disabled; },
    set disabled(v: boolean) {
      input.disabled = v; down.disabled = v; up.disabled = v;
      setClass(wrap, 'fc-off', v);
    },
    focus: () => input.focus?.(),
    addEventListener(type, fn) { (type === 'change' ? changeFns : inputFns).push(fn); },
  };
}

// ═══════════════════════════════════════════════════════════ 3. TOGGLE

export interface FcToggleOpts {
  id?: string;
  /** The accessible name. Also the visible text when `text` is not given. */
  label: string;
  /** Visible text beside the track. Pass '' for a bare switch. */
  text?: string;
  checked?: boolean;
  cls?: string;
  skin?: FcSkin;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
}

export interface FcToggleCtl {
  el: HTMLButtonElement;
  /** Reads/writes like `HTMLInputElement.checked`. */
  checked: boolean;
  disabled: boolean;
  focus(): void;
  addEventListener(type: 'change', fn: () => void): void;
}

/**
 * The ruling: everything that is not a GRID BULK-SELECT is a toggle. The
 * audit measured 20 checkboxes across five surfaces and ZERO bulk-select
 * instances — the carve-out is real but currently unexercised, so every one
 * of the 20 converts. `role="switch"` is the honest ARIA for a binary that
 * commits immediately (which all 20 do); a bulk-select row, if one is ever
 * built, keeps `type="checkbox"` and primitives-check's allowlist names it.
 */
export function mkFcToggle(opts: FcToggleOpts): FcToggleCtl {
  const skin = opts.skin ?? {};
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.setAttribute('role', 'switch');
  setCls(btn, cx('fc-tg', skin.wrap, opts.cls));
  if (opts.id) { btn.id = opts.id; btn.setAttribute?.('id', opts.id); }
  btn.setAttribute?.('aria-label', opts.label);
  const text = opts.text === undefined ? opts.label : opts.text;
  btn.innerHTML =
    `<span class="fc-tg-track"><span class="fc-tg-knob"></span></span>` +
    (text ? `<span class="fc-tg-text">${esc(text)}</span>` : '');

  const changeFns: Array<() => void> = [];
  let on = Boolean(opts.checked);
  const paint = (): void => {
    btn.setAttribute?.('aria-checked', String(on));
    btn.dataset.on = on ? '1' : '';
    setClass(btn, 'on', on);
  };
  paint();
  const flip = (): void => {
    if (btn.disabled) return;
    on = !on;
    paint();
    opts.onChange?.(on);
    for (const fn of changeFns) fn();
  };
  btn.addEventListener('click', flip);
  // A real <button> already fires click on Space/Enter; this only covers the
  // Space-scrolls-the-page default the button role suppresses inconsistently
  // when the control is inside a scrollable pane.
  btn.addEventListener('keydown', (e) => { if (e.key === ' ') e.preventDefault(); });
  if (opts.disabled) btn.disabled = true;

  return {
    el: btn,
    get checked() { return on; },
    set checked(v: boolean) { if (v !== on) { on = v; paint(); } },
    get disabled() { return btn.disabled; },
    set disabled(v: boolean) { btn.disabled = v; setClass(btn, 'fc-off', v); },
    focus: () => btn.focus?.(),
    addEventListener(_type, fn) { changeFns.push(fn); },
  };
}

/**
 * The toggle's HTML-STRING lane, for a list that re-renders wholesale from a
 * template (the accounts pane) rather than holding controllers. It emits the
 * identical DOM `mkFcToggle` builds; the host flips `aria-checked` itself in
 * its delegated click handler, exactly as it used to let a native checkbox
 * flip `checked` before the patch.
 *
 * The parity claim in the line above is DRIVEN, not asserted:
 * `helm-console/scripts/fc-toggle-parity.test.mjs` builds both lanes from
 * THIS module and compares class set / aria-checked / data-on / inner
 * markup, checked and unchecked. It used to be false on exactly one
 * attribute — `mkFcToggle` stamps `.on` (`setClass(btn, 'on', on)`) and this
 * lane emitted only `aria-checked` + `data-on`, so under Synthwave '88 the
 * resting bloom, which R16's glow budget licenses through the LIT CLASS
 * `on` and not through `[aria-checked="true"]`, painted on controller-built
 * switches and not on string-built ones. Two visibly different toggles under
 * one style, decided by which lane built them (#1159). A host that flips the
 * state in place must flip all three: `aria-checked`, `data-on`, and `.on`.
 */
export function fcToggleHtml(spec: {
  label: string;
  text?: string;
  checked?: boolean;
  disabled?: boolean;
  cls?: string;
  id?: string;
  attrs?: Record<string, string | number | boolean | null | undefined>;
}): string {
  const on = Boolean(spec.checked);
  const text = spec.text === undefined ? '' : spec.text;
  const extra = Object.entries(spec.attrs ?? {})
    .filter(([, v]) => v !== null && v !== undefined && v !== false)
    .map(([k, v]) => (v === true ? ` ${k}` : ` ${k}="${esc(String(v))}"`))
    .join('');
  return (
    `<button type="button" role="switch" class="${esc(cx('fc-tg', spec.cls, on && 'on'))}"` +
    (spec.id ? ` id="${esc(spec.id)}"` : '') +
    ` aria-checked="${on}" aria-label="${esc(spec.label)}" data-tip="${esc(spec.label)}"` +
    (on ? ' data-on="1"' : '') + (spec.disabled ? ' disabled' : '') + extra +
    '><span class="fc-tg-track"><span class="fc-tg-knob"></span></span>' +
    (text ? `<span class="fc-tg-text">${esc(text)}</span>` : '') +
    '</button>'
  );
}

// ═══════════════════════════════════════════════════════════ 4. TEXT / SEARCH

export interface FcTextOpts {
  id?: string;
  label: string;
  placeholder?: string;
  value?: string;
  /** 'search' adds the leading glyph, the clear affordance and the typing
   *  state — the mkSearchBox ladder, now one lane of the text primitive. */
  variant?: 'text' | 'search';
  /** A leading sprite glyph for the plain variant. */
  glyph?: string;
  /** Reserve the in-field validity-icon seat (the create-dialog treatment,
   *  generalized). `validity()` writes into it. */
  validity?: boolean;
  cls?: string;
  skin?: FcSkin;
  disabled?: boolean;
  spellcheck?: boolean;
  autocomplete?: string;
  onInput?: (value: string) => void;
}

export interface FcTextCtl {
  el: HTMLElement;
  input: HTMLInputElement;
  /** The in-field validity seat (present when `validity: true`). */
  vseat: HTMLElement | null;
  /** The visually-hidden aria-live line paired with the seat. */
  live: HTMLElement | null;
  value: string;
  disabled: boolean;
  clear(): void;
  focus(): void;
}

/** How long the 'typing' state outlives the last keystroke. */
export const FC_TYPING_MS = 350;

export function mkFcText(opts: FcTextOpts): FcTextCtl {
  const skin = opts.skin ?? {};
  const search = opts.variant === 'search';
  const wrap = mk('div', cx('fc', 'fc-text', 'fc-field', search && 'fc-search', skin.wrap, opts.cls));
  const glyph = opts.glyph ?? (search ? 'search' : '');
  if (glyph) wrap.innerHTML = svg(glyph, cx('fc-text-ico', skin.ico));

  const input = document.createElement('input');
  setCls(input, cx('fc-text-input', skin.input));
  // type="text" even for search: `type=search` renders WebKit's own cancel
  // glyph, which doubles the clear affordance below and cannot theme.
  input.type = 'text';
  if (search) input.setAttribute?.('role', 'searchbox');
  if (opts.id) { input.id = opts.id; input.setAttribute?.('id', opts.id); }
  if (opts.placeholder) input.placeholder = opts.placeholder;
  input.setAttribute?.('aria-label', opts.label);
  if (opts.autocomplete) input.setAttribute?.('autocomplete', opts.autocomplete);
  if (opts.spellcheck === false) input.setAttribute?.('spellcheck', 'false');
  if (opts.value) input.value = opts.value;
  wrap.appendChild(input);

  let vseat: HTMLElement | null = null;
  let live: HTMLElement | null = null;
  if (opts.validity) {
    vseat = mk('span', 'fc-vicon');
    wrap.appendChild(vseat);
    live = mk('span', 'fc-live');
    live.setAttribute?.('aria-live', 'polite');
  }

  // The clear affordance IS an action (it empties the box and re-fires the
  // caller's lane), so it rides the ICON-BUTTON primitive — the sibling
  // module in this same package, not a hand-built button. One button
  // machine, and buttons-check counts this site on-system.
  let clearBtn: HTMLButtonElement | null = null;
  if (search) {
    clearBtn = mkIconBtn({
      glyph: 'x', label: 'Clear search', size: 'sm', cls: cx('fc-clear', skin.clear),
    });
    clearBtn.hidden = true;
    wrap.appendChild(clearBtn);
  }

  let typingTimer: number | null = null;
  const sync = (): void => {
    const has = input.value.length > 0;
    setClass(wrap, 'fc-has', has);
    if (skin.has) setClass(wrap, skin.has, has);
    if (clearBtn) clearBtn.hidden = !has;
  };
  const clear = (): void => {
    if (input.value === '') return; // nothing to clear — no phantom onInput
    input.value = '';
    sync();
    opts.onInput?.('');
  };
  input.addEventListener('input', () => {
    sync();
    if (search) {
      wrap.classList?.add('fc-typing');
      if (skin.typing) wrap.classList?.add(skin.typing);
      if (typingTimer) window.clearTimeout(typingTimer);
      typingTimer = window.setTimeout(() => {
        typingTimer = null;
        wrap.classList?.remove('fc-typing');
        if (skin.typing) wrap.classList?.remove(skin.typing);
      }, FC_TYPING_MS);
    }
    opts.onInput?.(input.value);
  });
  clearBtn?.addEventListener('click', () => {
    clear();
    input.focus?.(); // the person is mid-search; never strand focus on a
    // control that just hid itself
  });
  input.addEventListener('keydown', (e) => {
    if (search && e.key === 'Escape' && input.value !== '') { e.stopPropagation(); clear(); }
  });
  if (opts.disabled) input.disabled = true;
  sync();

  return {
    el: wrap,
    input,
    vseat,
    live,
    get value() { return input.value; },
    set value(v: string) { input.value = v; sync(); },
    get disabled() { return input.disabled; },
    set disabled(v: boolean) { input.disabled = v; setClass(wrap, 'fc-off', v); },
    clear,
    focus: () => input.focus?.(),
  };
}

// ── the in-field validity icon, generalized from create-dialog ────────────

export interface FcValidityVm {
  kind: 'none' | 'ok' | 'bad';
  /** The verdict SENTENCE. Rides data-tip + aria-label + the live line — one
   *  string, three seats, never a native title=. */
  tip?: string | null;
}

/**
 * The create-dialog treatment (D3), lifted out so every text field in the
 * product gets the same one: no verdict prose under the field, an in-field
 * icon whose entrance and exit are registered animations, and the sentence
 * on the tooltip + aria-label + a visually-hidden live line.
 *
 * `reducedMotion` is injected because each surface answers it differently
 * (the console's motion.ts, the Helm's effects-seat.ts) and this layer must
 * not pick one.
 */
export function mkFcValidityWriter(
  reducedMotion: () => boolean = () => false,
  leaveFallbackMs = 400,
  /** Class prefix. The console's create dialog authored this treatment under
   *  `hc-vicon`, and its rules are registered by NAME in that stylesheet's
   *  reduced-motion guard, so the writer takes the prefix rather than
   *  forcing a rename that motion-coverage would then have to chase. */
  prefix = 'fc-vicon',
  /** Glyph pair. Defaults to the sprite ids the fc field seat uses. */
  glyphs: { ok: string; bad: string } = { ok: 'check', bad: 'x' },
): (seat: HTMLElement, live: HTMLElement | null, vm: FcValidityVm) => void {
  const IN = `${prefix}-in`;
  const OUT = `${prefix}-out`;
  const OK = `${prefix}-ok`;
  const BAD = `${prefix}-bad`;
  const shown = new Map<HTMLElement, string>();
  return (seat, live, vm) => {
    const key = `${vm.kind}|${vm.tip ?? ''}`;
    if (shown.get(seat) === key) return;
    const prev = shown.get(seat)?.split('|')[0] ?? 'none';
    shown.set(seat, key);
    if (live) live.textContent = vm.tip ?? '';
    if (vm.tip) {
      seat.setAttribute?.('data-tip', vm.tip);
      seat.setAttribute?.('aria-label', vm.tip);
    } else {
      seat.removeAttribute?.('data-tip');
      seat.removeAttribute?.('aria-label');
    }
    if (vm.kind === 'none') {
      if (prev === 'none') return;
      if (reducedMotion()) {
        seat.classList?.remove(IN, OK, BAD);
        seat.innerHTML = '';
        return;
      }
      // Animated exit: play the registered fade, then clear the art.
      seat.classList?.remove(IN);
      seat.classList?.add(OUT);
      let done = false;
      const clear = (): void => {
        if (done) return;
        done = true;
        // Only clear if no NEW verdict landed while the exit played.
        if (shown.get(seat) === key) {
          seat.classList?.remove(OUT, OK, BAD);
          seat.innerHTML = '';
        }
      };
      seat.addEventListener?.('animationend', clear, { once: true } as AddEventListenerOptions);
      setTimeout(clear, leaveFallbackMs);
      return;
    }
    seat.innerHTML = svg(vm.kind === 'ok' ? glyphs.ok : glyphs.bad, `${prefix}-glyph`);
    seat.classList?.remove(OUT, IN, OK, BAD);
    // Restart the entrance on verdict change (ok -> bad re-pops).
    void (seat as { offsetWidth?: number }).offsetWidth;
    seat.classList?.add(vm.kind === 'ok' ? OK : BAD, IN);
  };
}

// ═══════════════════════════════════════════════════════════ 5. COMBOBOX

export interface FcComboOption {
  value: string;
  label: string;
  /** A sprite glyph for the row (the branch typeahead's folder/branch pair). */
  glyph?: string;
  /** A trailing count/hint chip. */
  hint?: string;
  /** True when accepting the row should REOPEN the list (a directory segment
   *  that has children), rather than close it. */
  drill?: boolean;
}

export interface FcComboVm {
  options: FcComboOption[];
  total: number;
  overflow: number;
}

export interface FcTypeaheadSkin {
  pop?: string;
  item?: string;
  label?: string;
  hint?: string;
  active?: string;
  more?: string;
  drill?: string;
}

export interface FcTypeaheadOpts {
  /** Pure: the current query in, the rows to show out. The CALLER owns what
   *  "matches" means (prefix, fuzzy, server-side) — this primitive owns the
   *  list, the ARIA and the keyboard. */
  suggest: (query: string) => FcComboVm;
  /** Accessible name for the list. */
  label: string;
  /** Text under the overflow row. Receives the vm. */
  overflowText?: (vm: FcComboVm) => string;
  /** A row was accepted (Enter / Tab / click). */
  onAccept?: (option: FcComboOption) => void;
  /** Fired after an accept writes the value, so the host can re-judge. */
  onInput?: (value: string) => void;
  popId?: string;
  skin?: FcTypeaheadSkin;
}

export interface FcTypeaheadCtl {
  pop: HTMLElement;
  /** Re-run `suggest` and repaint. `keepActive` preserves the active row. */
  render(keepActive?: boolean): void;
  close(): void;
}

/**
 * ATTACH the typeahead list to an existing text input — generalized out of
 * the create-release source-branch field (#452-era), which was the only
 * combobox in the product and could not be reused because it was welded into
 * one dialog's DOM.
 *
 * The contract it carries over verbatim: `aria-autocomplete=list`,
 * `aria-controls`, `aria-expanded` and `aria-activedescendant` on the input,
 * a `role=listbox` of `role=option` rows, ArrowDown opens, arrows rove,
 * Enter/Tab accept the active row (Tab COMPLETES, it never leaves the
 * field), Enter with no active row never submits through an open list, and
 * Escape closes the LIST only so a host dialog's Escape stays one level up.
 * The pop is an absolute OVERLAY: opening it must move no layout, which is
 * what lets it live inside a fixed-size modal.
 */
export function attachFcTypeahead(
  input: HTMLInputElement, host: HTMLElement, opts: FcTypeaheadOpts,
): FcTypeaheadCtl {
  const skin = opts.skin ?? {};
  const popId = opts.popId ?? `${input.id || 'fcCombo'}Pop`;
  input.setAttribute?.('aria-autocomplete', 'list');
  input.setAttribute?.('aria-controls', popId);
  input.setAttribute?.('aria-expanded', 'false');

  const pop = mk('div', cx('fc-pop', 'fc-combo-pop', skin.pop));
  pop.id = popId;
  pop.setAttribute?.('id', popId);
  pop.setAttribute?.('role', 'listbox');
  pop.setAttribute?.('aria-label', `${opts.label} suggestions`);
  pop.hidden = true; // absolute overlay — layout-neutral by design
  host.appendChild(pop);

  const ACTIVE = skin.active ?? 'fc-opt-active';
  let vm: FcComboVm = { options: [], total: 0, overflow: 0 };
  let active = -1;

  const close = (): void => {
    pop.hidden = true;
    active = -1;
    input.setAttribute?.('aria-expanded', 'false');
    input.removeAttribute?.('aria-activedescendant');
  };

  const rows = (): HTMLElement[] =>
    Array.from(pop.querySelectorAll?.<HTMLElement>(`.${(skin.item ?? 'fc-opt').split(/\s+/)[0]}`) ?? []);

  const paintActive = (): void => {
    const list = rows();
    list.forEach((r, i) => {
      if (i === active) r.classList?.add(ACTIVE);
      else r.classList?.remove(ACTIVE);
      r.setAttribute?.('aria-selected', i === active ? 'true' : 'false');
    });
    const cur = active >= 0 ? list[active] : null;
    if (cur) {
      input.setAttribute?.('aria-activedescendant', cur.id);
      cur.scrollIntoView?.({ block: 'nearest' });
    } else input.removeAttribute?.('aria-activedescendant');
  };

  const accept = (index: number): void => {
    const opt = vm.options[index];
    if (!opt) return;
    input.value = opt.value;
    opts.onAccept?.(opt);
    opts.onInput?.(input.value);
    if (opt.drill) render(true); // a directory opens its next segment set
    else close();
    input.focus?.();
  };

  function render(keepActive = false): void {
    vm = opts.suggest(input.value);
    pop.replaceChildren?.();
    if (vm.options.length === 0) { close(); return; }
    vm.options.forEach((o, i) => {
      // An option ROW of this primitive's listbox (see the select's note).
      const row = document.createElement('button');
      row.type = 'button';
      row.setAttribute('role', 'option');
      setCls(row, cx('fc-opt', skin.item, o.drill && cx('fc-opt-drill', skin.drill)));
      row.id = `${popId}-opt-${i}`;
      row.setAttribute?.('id', row.id);
      row.setAttribute?.('aria-selected', 'false');
      row.tabIndex = -1;
      if (o.glyph) row.innerHTML = svg(o.glyph, 'fc-opt-ico');
      row.appendChild(mk('span', cx('fc-opt-label', skin.label), o.label));
      if (o.hint) row.appendChild(mk('span', cx('fc-opt-hint', skin.hint), o.hint));
      row.addEventListener('click', () => accept(i));
      pop.appendChild(row);
    });
    if (vm.overflow > 0) {
      pop.appendChild(mk('div', cx('fc-opt-more', skin.more),
        opts.overflowText?.(vm) ?? `+${vm.overflow} more (${vm.total} total) — keep typing`));
    }
    active = keepActive && active >= 0 ? Math.min(active, vm.options.length - 1) : -1;
    pop.hidden = false;
    input.setAttribute?.('aria-expanded', 'true');
    paintActive();
  }

  input.addEventListener('keydown', (e: KeyboardEvent) => {
    const open = !pop.hidden;
    if (e.key === 'ArrowDown') {
      e.preventDefault?.();
      if (!open) render(false);
      if (!pop.hidden) { active = Math.min(active + 1, vm.options.length - 1); paintActive(); }
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowUp') {
      e.preventDefault?.();
      active = Math.max(active - 1, 0);
      paintActive();
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault?.();
      active = e.key === 'Home' ? 0 : vm.options.length - 1;
      paintActive();
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (active >= 0) { e.preventDefault?.(); accept(active); } // Tab completes
      else if (e.key === 'Enter') { e.preventDefault?.(); close(); } // never submit through an open list
      else close();
    } else if (e.key === 'Escape') {
      // Close the LIST only — a host dialog's Escape stays one level up.
      e.preventDefault?.();
      e.stopPropagation?.();
      close();
    }
  });
  input.addEventListener('focus', () => { if (input.value.trim()) render(false); });

  return { pop, render, close };
}

export interface FcComboOpts extends FcTypeaheadOpts {
  id?: string;
  placeholder?: string;
  value?: string;
  validity?: boolean;
  cls?: string;
  skin?: FcTypeaheadSkin & FcSkin;
}

export interface FcComboCtl extends FcTextCtl {
  pop: HTMLElement;
  render(keepActive?: boolean): void;
  closeList(): void;
}

/** The whole combobox: the text field plus its typeahead list. */
export function mkFcCombobox(opts: FcComboOpts): FcComboCtl {
  const base = mkFcText({
    id: opts.id, label: opts.label, placeholder: opts.placeholder, value: opts.value,
    validity: opts.validity, cls: cx('fc-combo', opts.cls), skin: opts.skin,
    spellcheck: false, autocomplete: 'off',
    onInput: (v) => { opts.onInput?.(v); ta.render(false); },
  });
  const ta = attachFcTypeahead(base.input, base.el, opts);
  return { ...base, pop: ta.pop, render: ta.render, closeList: ta.close };
}

// ═══════════════════════════════════════════════════════════ 6. DATE / TIME

export type FcDateMode = 'date' | 'time' | 'datetime';

export interface FcDateTimeOpts {
  id?: string;
  label: string;
  mode?: FcDateMode;
  /** 'YYYY-MM-DD', 'HH:MM', or 'YYYY-MM-DD HH:MM'. */
  value?: string;
  cls?: string;
  skin?: FcSkin;
  disabled?: boolean;
  onChange?: (value: string) => void;
}

export interface FcDateTimeCtl {
  el: HTMLElement;
  /** ISO-ish text, '' when incomplete. */
  value: string;
  disabled: boolean;
  focus(): void;
  addEventListener(type: 'change', fn: () => void): void;
}

interface SegSpec { key: string; len: number; min: number; max: number; pad: number }

const DATE_SEGS: SegSpec[] = [
  { key: 'Y', len: 4, min: 1970, max: 2999, pad: 4 },
  { key: 'M', len: 2, min: 1, max: 12, pad: 2 },
  { key: 'D', len: 2, min: 1, max: 31, pad: 2 },
];
const TIME_SEGS: SegSpec[] = [
  { key: 'h', len: 2, min: 0, max: 23, pad: 2 },
  { key: 'm', len: 2, min: 0, max: 59, pad: 2 },
];

/**
 * The date/time primitive. The audit proved ZERO native date/time inputs
 * exist today across all five surfaces (positive-controlled), so this ships
 * not to convert anything but so the NEXT feature that needs one never
 * reaches for `<input type="date">` — whose picker is OS chrome, is the one
 * control whose look is completely outside a style system's reach, and whose
 * absence is exactly why nobody noticed it was banned.
 *
 * Deliberately lean: segmented numeric entry, no calendar grid. Segments are
 * separate tab-less inputs the composite roves between; Up/Down step the
 * focused segment, digits auto-advance, Backspace walks back, and the value
 * is only reported once every segment is filled.
 */
export function mkFcDateTime(opts: FcDateTimeOpts): FcDateTimeCtl {
  const mode: FcDateMode = opts.mode ?? 'date';
  const specs = mode === 'time' ? TIME_SEGS : mode === 'date' ? DATE_SEGS : [...DATE_SEGS, ...TIME_SEGS];
  const skin = opts.skin ?? {};
  const wrap = mk('div', cx('fc', 'fc-dt', 'fc-field', skin.wrap, opts.cls));
  wrap.setAttribute?.('role', 'group');
  wrap.setAttribute?.('aria-label', opts.label);
  if (opts.id) { wrap.id = opts.id; wrap.setAttribute?.('id', opts.id); }
  wrap.innerHTML = svg(mode === 'time' ? 'clock' : 'history', 'fc-dt-ico');

  const segs: HTMLInputElement[] = [];
  const changeFns: Array<() => void> = [];

  const readValue = (): string => {
    if (segs.some((s) => s.value.length === 0)) return '';
    const v = segs.map((s, i) => s.value.padStart(specs[i]!.pad, '0'));
    if (mode === 'time') return `${v[0]}:${v[1]}`;
    if (mode === 'date') return `${v[0]}-${v[1]}-${v[2]}`;
    return `${v[0]}-${v[1]}-${v[2]} ${v[3]}:${v[4]}`;
  };
  const fire = (): void => {
    const v = readValue();
    opts.onChange?.(v);
    for (const fn of changeFns) fn();
  };

  specs.forEach((spec, i) => {
    if (i > 0) {
      const sep = mode === 'time' ? ':' : i === 3 ? ' ' : i > 3 ? ':' : '-';
      wrap.appendChild(mk('span', 'fc-dt-sep', sep));
    }
    const el = document.createElement('input');
    setCls(el, cx('fc-dt-seg', `fc-dt-${spec.key}`, skin.input));
    el.type = 'text';
    el.setAttribute?.('inputmode', 'numeric');
    el.setAttribute?.('autocomplete', 'off');
    el.setAttribute?.('role', 'spinbutton');
    el.setAttribute?.('aria-label', `${opts.label} ${spec.key}`);
    el.setAttribute?.('aria-valuemin', String(spec.min));
    el.setAttribute?.('aria-valuemax', String(spec.max));
    el.setAttribute?.('maxlength', String(spec.len));
    el.placeholder = spec.key.repeat(spec.len);
    el.addEventListener('input', () => {
      el.value = el.value.replace(/\D+/g, '').slice(0, spec.len);
      if (el.value.length === spec.len) segs[i + 1]?.focus?.();
      fire();
    });
    el.addEventListener('keydown', (e) => {
      const cur = el.value === '' ? spec.min : Number(el.value);
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const span = spec.max - spec.min + 1;
        const next = spec.min + (((cur - spec.min + (e.key === 'ArrowUp' ? 1 : -1)) % span) + span) % span;
        el.value = String(next).padStart(spec.pad, '0');
        el.setAttribute?.('aria-valuenow', el.value);
        fire();
      } else if (e.key === 'ArrowRight' && (el.selectionStart ?? 0) >= el.value.length) {
        segs[i + 1]?.focus?.();
      } else if (e.key === 'ArrowLeft' && (el.selectionStart ?? 0) === 0) {
        segs[i - 1]?.focus?.();
      } else if (e.key === 'Backspace' && el.value === '') {
        segs[i - 1]?.focus?.();
      }
    });
    el.addEventListener('blur', () => {
      if (el.value === '') return;
      const n = Math.min(spec.max, Math.max(spec.min, Number(el.value)));
      el.value = String(n).padStart(spec.pad, '0');
      el.setAttribute?.('aria-valuenow', el.value);
      fire();
    });
    segs.push(el);
    wrap.appendChild(el);
  });

  if (opts.value) {
    const digits = opts.value.replace(/\D+/g, '');
    let at = 0;
    for (let i = 0; i < segs.length; i++) {
      const spec = specs[i]!;
      segs[i]!.value = digits.slice(at, at + spec.len);
      at += spec.len;
    }
  }
  if (opts.disabled) for (const s of segs) s.disabled = true;

  return {
    el: wrap,
    get value() { return readValue(); },
    set value(v: string) {
      const digits = String(v).replace(/\D+/g, '');
      let at = 0;
      for (let i = 0; i < segs.length; i++) {
        const spec = specs[i]!;
        segs[i]!.value = digits.slice(at, at + spec.len);
        at += spec.len;
      }
    },
    get disabled() { return Boolean(segs[0]?.disabled); },
    set disabled(v: boolean) { for (const s of segs) s.disabled = v; setClass(wrap, 'fc-off', v); },
    focus: () => segs[0]?.focus?.(),
    addEventListener(_type, fn) { changeFns.push(fn); },
  };
}

// ═══════════════════════════════════════════════════════ 7. THE SEAT LANE

/**
 * Most of this codebase renders markup from backtick templates and wires it
 * afterwards by id (the Helm's Settings modal is 4,000 lines of exactly
 * that). A control that can only be CONSTRUCTED would force every one of
 * those call sites to be rewritten into DOM building — so the primitives
 * also have a SEAT lane: the template writes an empty seat, and the wiring
 * pass hydrates it in place and gets the controller back.
 *
 * The seat is a plain <div>, so nothing native ever reaches the DOM even for
 * the instant before hydration.
 */
export interface FcSeatSpec {
  id: string;
  family: FcFamily;
  label: string;
  /** select: the options, as `value:label` pairs rendered into the seat. */
  options?: FcOption[];
  value?: string;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  text?: string;
  placeholder?: string;
  mode?: FcDateMode;
  cls?: string;
}

const seatData = (spec: FcSeatSpec): string => {
  const bits: Array<[string, string]> = [
    ['data-fc', spec.family],
    ['data-fc-id', spec.id],
    ['data-fc-label', spec.label],
  ];
  if (spec.value !== undefined) bits.push(['data-fc-value', spec.value]);
  if (spec.min !== undefined) bits.push(['data-fc-min', String(spec.min)]);
  if (spec.max !== undefined) bits.push(['data-fc-max', String(spec.max)]);
  if (spec.step !== undefined) bits.push(['data-fc-step', String(spec.step)]);
  if (spec.unit) bits.push(['data-fc-unit', spec.unit]);
  if (spec.text !== undefined) bits.push(['data-fc-text', spec.text]);
  if (spec.placeholder) bits.push(['data-fc-placeholder', spec.placeholder]);
  if (spec.mode) bits.push(['data-fc-mode', spec.mode]);
  if (spec.options?.length) {
    bits.push(['data-fc-options', JSON.stringify(spec.options.map((o) => [o.value, o.label]))]);
  }
  return bits.map(([k, v]) => `${k}="${esc(v)}"`).join(' ');
};

/** The HTML-string lane: an empty, inert seat the wiring pass hydrates. */
export function fcSeat(spec: FcSeatSpec): string {
  return `<div class="fc-seat${spec.cls ? ` ${esc(spec.cls)}` : ''}" ${seatData(spec)}></div>`;
}

function readSeat(root: ParentNode, id: string): HTMLElement | null {
  return root.querySelector<HTMLElement>(`[data-fc-id="${id}"]`);
}

function seatOptions(seat: HTMLElement): FcOption[] {
  const raw = seat.dataset?.fcOptions ?? seat.getAttribute?.('data-fc-options') ?? '';
  if (!raw) return [];
  try {
    return (JSON.parse(raw) as Array<[string, string]>).map(([value, label]) => ({ value, label }));
  } catch { return []; }
}

const seatAttr = (seat: HTMLElement, k: string): string | undefined => {
  const v = seat.getAttribute?.(`data-fc-${k}`);
  return v === null || v === undefined ? undefined : v;
};
const seatNum = (seat: HTMLElement, k: string): number | undefined => {
  const v = seatAttr(seat, k);
  return v === undefined || v === '' ? undefined : Number(v);
};

/** Replace `seat` with `el`, carrying the seat's own layout classes over. */
function mountInSeat(seat: HTMLElement, el: HTMLElement): void {
  for (const c of (seat.className || '').split(/\s+/)) {
    if (c && c !== 'fc-seat') el.classList?.add(c);
  }
  seat.replaceWith?.(el);
}

/** Hydrate a `select` seat. Returns null when the seat is absent (a caller
 *  guarding on a possibly-unmounted pane keeps its `?.` shape). */
export function fcSelect(
  root: ParentNode, id: string, extra?: Partial<FcSelectOpts>,
): FcSelectCtl | null {
  const seat = readSeat(root, id);
  if (!seat) return null;
  const ctl = mkFcSelect({
    id,
    label: seatAttr(seat, 'label') ?? id,
    options: seatOptions(seat),
    value: seatAttr(seat, 'value') ?? '',
    ...extra,
  });
  mountInSeat(seat, ctl.el);
  return ctl;
}

export function fcStepper(
  root: ParentNode, id: string, extra?: Partial<FcStepperOpts>,
): FcStepperCtl | null {
  const seat = readSeat(root, id);
  if (!seat) return null;
  const ctl = mkFcStepper({
    id,
    label: seatAttr(seat, 'label') ?? id,
    value: seatAttr(seat, 'value'),
    min: seatNum(seat, 'min'),
    max: seatNum(seat, 'max'),
    step: seatNum(seat, 'step'),
    unit: seatAttr(seat, 'unit'),
    placeholder: seatAttr(seat, 'placeholder'),
    ...extra,
  });
  mountInSeat(seat, ctl.el);
  return ctl;
}

export function fcToggle(
  root: ParentNode, id: string, extra?: Partial<FcToggleOpts>,
): FcToggleCtl | null {
  const seat = readSeat(root, id);
  if (!seat) return null;
  const ctl = mkFcToggle({
    id,
    label: seatAttr(seat, 'label') ?? id,
    text: seatAttr(seat, 'text'),
    checked: seatAttr(seat, 'value') === '1',
    ...extra,
  });
  mountInSeat(seat, ctl.el);
  return ctl;
}

export function fcText(
  root: ParentNode, id: string, extra?: Partial<FcTextOpts>,
): FcTextCtl | null {
  const seat = readSeat(root, id);
  if (!seat) return null;
  const ctl = mkFcText({
    id,
    label: seatAttr(seat, 'label') ?? id,
    placeholder: seatAttr(seat, 'placeholder'),
    value: seatAttr(seat, 'value'),
    ...extra,
  });
  mountInSeat(seat, ctl.el);
  return ctl;
}

export function fcDateTime(
  root: ParentNode, id: string, extra?: Partial<FcDateTimeOpts>,
): FcDateTimeCtl | null {
  const seat = readSeat(root, id);
  if (!seat) return null;
  const ctl = mkFcDateTime({
    id,
    label: seatAttr(seat, 'label') ?? id,
    mode: (seatAttr(seat, 'mode') as FcDateMode | undefined) ?? 'date',
    value: seatAttr(seat, 'value'),
    ...extra,
  });
  mountInSeat(seat, ctl.el);
  return ctl;
}
